import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import { getPrivacyNotice, getAvailableLanguages, replacePlaceholders } from '../utils/privacyNotices-i18n';
import { Globe } from 'lucide-react';

export default function PrivacyPage() {
    const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
    const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'support@eternallocker.com';
    const grievanceEmail = process.env.REACT_APP_GRIEVANCE_EMAIL || 'grievance@eternallocker.com';
    const grievanceOfficerName = process.env.REACT_APP_GRIEVANCE_OFFICER_NAME || 'Y. R. Gandhi';
    const grievanceOfficerTitle = process.env.REACT_APP_GRIEVANCE_OFFICER_TITLE || 'Grievance Officer';
    
    const [language, setLanguage] = useState(() => {
        return localStorage.getItem('language') || 'en';
    });

    const availableLanguages = getAvailableLanguages();
    const content = getPrivacyNotice(language);

    useEffect(() => {
        document.title = language === 'en' ? "Privacy Policy | Eternal Locker™" : "गोपनीयता नीति | Eternal Locker™";
        localStorage.setItem('language', language);
    }, [language]);

    return (
        <>
            <Header type="small" showAccountDropdown={isLoggedIn} />
            <div className="flex justify-center w-full font-sans">
                <div className="w-full max-w-7xl p-8 space-y-6 bg-background-light shadow-2xl rounded-xl border border-accent-main">
                    {/* Language Selector */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <h1 className="text-3xl font-bold text-accent-main">{content.title}</h1>
                        <div className="flex items-center gap-2">
                            <Globe className="w-5 h-5 text-accent-main" />
                            <select 
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                                className="px-3 py-2 border border-accent-main rounded bg-background-light text-accent-main text-sm font-medium hover:bg-primary-light cursor-pointer"
                            >
                                {availableLanguages.map(lang => (
                                    <option key={lang} value={lang}>
                                        {lang === 'en' ? 'English' : lang === 'hi' ? 'हिंदी' : lang.toUpperCase()}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Content Sections */}
                    <div className="flex flex-col gap-8 p-4 bg-primary-light border border-accent-main rounded-xl shadow">
                        {/* Introduction */}
                        <div className="text-lg text-justify space-y-2">
                            <p>{content.intro.p1}</p>
                            <p>
                                {language === 'en' ? 'For details about your rights and obligations, please also review our ' : 'आपके अधिकारों और दायित्वों के बारे में विवरण के लिए, कृपया हमारी '}
                                <a className="text-accent-main underline" href="/terms">
                                    {language === 'en' ? 'Terms and Conditions' : 'शर्तें और शर्तें'}
                                </a>.
                            </p>
                        </div>

                        {/* Dynamic Sections */}
                        {Object.entries(content.sections).map(([key, section]) => (
                            <div key={key}>
                                <h2 className="text-2xl font-semibold text-accent-main">{section.title}</h2>
                                <ul className="list-disc list-inside space-y-2 text-justify mt-3">
                                    {section.content.map((item, idx) => (
                                        <li key={idx}>
                                            {replacePlaceholders(item, { 
                                                GRIEVANCE_OFFICER_NAME: grievanceOfficerName, 
                                                GRIEVANCE_OFFICER_TITLE: grievanceOfficerTitle,
                                                GRIEVANCE_EMAIL: grievanceEmail,
                                                SUPPORT_EMAIL: supportEmail
                                            })}
                                        </li>
                                    ))}
                                </ul>
                                {section.note && (
                                    <p className="mt-3 text-justify text-primary-700 bg-background-light p-3 rounded border border-accent-main">
                                        {replacePlaceholders(section.note, { 
                                            GRIEVANCE_OFFICER_NAME: grievanceOfficerName, 
                                            GRIEVANCE_OFFICER_TITLE: grievanceOfficerTitle,
                                            GRIEVANCE_EMAIL: grievanceEmail,
                                            SUPPORT_EMAIL: supportEmail
                                        })}
                                    </p>
                                )}
                            </div>
                        ))}

                        {/* Footer Info */}
                        <div className="text-sm border-t border-accent-main pt-4 mt-6">
                            <p className="mb-2">
                                {language === 'en' 
                                    ? 'Eternal Locker™ is a service of Nybble Technologies Private Limited. View our ' 
                                    : 'Eternal Locker™ Nybble Technologies Private Limited की सेवा है। हमारी '}
                                <a 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-accent-main underline" 
                                    href="https://www.nybbletechnologies.com/privacy.html"
                                >
                                    {language === 'en' ? 'Master Privacy Policy' : 'मास्टर गोपनीयता नीति'}
                                </a>.
                            </p>
                        </div>
                        <div className="text-sm text-right text-gray-400">
                            <p>{language === 'en' ? 'Last updated: April 2026' : 'अंतिम अपडेट: अप्रैल 2026'}</p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
